/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/label-has-associated-control, react/no-unescaped-entities */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getEvents, removeEvent, updateEvent } from '../slice/eventSlice';
import { FaEdit, FaTrash, FaPlus, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import ConfirmModal from '../common/ConfirmModal';
import './EventList.css';

const EventList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { data: events = [], loading } = useSelector((state) => state.events);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  
  // Custom Confirmation Modal State
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  useEffect(() => {
    dispatch(getEvents());
  }, [dispatch]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedEvents(events.map(ev => ev.id));
    } else {
      setSelectedEvents([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedEvents.includes(id)) {
      setSelectedEvents(selectedEvents.filter(item => item !== id));
    } else {
      setSelectedEvents([...selectedEvents, id]);
    }
  };

  const handleToggleStatus = (eventItem) => {
    const updatedStatus = eventItem.status === 1 ? 0 : 1;
    dispatch(updateEvent({
      id: eventItem.id,
      data: {
        ...eventItem,
        status: updatedStatus
      }
    }));
  };

  const triggerDeleteConfirm = (e, ev) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteTarget(ev);
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      dispatch(removeEvent(deleteTarget.id));
      toast.success('Event deleted successfully');
      setDeleteTarget(null);
    }
  };

  const handleConfirmEdit = () => {
    if (editTarget) {
      const id = editTarget.id;
      setEditTarget(null);
      navigate(`/edit-event/${id}`);
    }
  };

  const getImageUrl = (image) => {
    if (!image) return 'https://via.placeholder.com/50x50?text=No+Img';
    const backendHost = process.env.REACT_APP_BACKEND 
      ? process.env.REACT_APP_BACKEND.replace(/\/$/, '') 
      : (process.env.REACT_APP_BACKEND_URL || '').split('/api')[0] || 'http://localhost:8000';
    return `${backendHost}/uploads/${image}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const formattedHours = String(hours).padStart(2, '0');
    return `${day}/${month}/${year} ${formattedHours}:${minutes} ${ampm}`;
  };

  const filteredEvents = events.filter(ev => 
    ev.title && ev.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEvents = filteredEvents.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <>
      <div className="premium-page-container animate-fade-up">
        {/* Header Section */}
        <div className="premium-page-header">
          <h1 className="page-title">Event Manager</h1>
          <button className="btn-create" onClick={() => navigate('/add-event')}>
            <FaPlus style={{ marginRight: '8px' }} /> Create
          </button>
        </div>

        {/* Search & Actions Panel */}
        <div className="premium-controls-bar">
          <div className="search-bar-wrapper search-box" style={{ margin: 0 }}>
            <FaSearch className="search-icon" />
            <input 
              type="text" 
              placeholder="Search here" 
              value={searchQuery}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
        </div>

        {/* Table Section */}
        <div className="premium-table-wrapper">
          {loading ? (
            <div className="loading-spinner" style={{ padding: '2rem', textAlign: 'center', color: '#c4a882' }}>Loading event directory...</div>
          ) : filteredEvents.length === 0 ? (
            <div className="empty-state" style={{ padding: '2rem', textAlign: 'center', color: '#c4a882' }}>No events found in directory</div>
          ) : (
              <table className="premium-table">
                <thead>
                  <tr>
                    <th style={{ width: '4%' }}>
                      <input 
                        type="checkbox" 
                        className="premium-checkbox"
                        checked={selectedEvents.length === events.length && events.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th style={{ width: '12%' }}>Status</th>
                    <th style={{ width: '32%' }}>Event Title</th>
                    <th style={{ width: '21%' }}>Created on</th>
                    <th style={{ width: '21%' }}>Last Updated on</th>
                    <th style={{ width: '10%', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                {currentEvents.map((ev) => (
                  <tr key={ev.id} className={selectedEvents.includes(ev.id) ? 'selected-row' : ''}>
                    <td>
                      <input 
                        type="checkbox" 
                        className="premium-checkbox"
                        checked={selectedEvents.includes(ev.id)}
                        onChange={() => handleSelectOne(ev.id)}
                      />
                    </td>
                    <td>
                      <div className="status-toggle-wrapper">
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label className="status-toggle" htmlFor={`status-toggle-${ev.id}`}>
                          <span style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', border: 0 }}>Toggle Status</span>
                          <input 
                            id={`status-toggle-${ev.id}`}
                            type="checkbox" 
                            checked={ev.status === 1} 
                            onChange={() => handleToggleStatus(ev)}
                          />
                          <span className="status-slider"></span>
                        </label>
                        <span className={`status-label ${ev.status === 1 ? 'active' : 'inactive'}`}>
                          {ev.status === 1 ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="event-title-cell">
                        <img 
                          src={getImageUrl(ev.thumb)} 
                          alt={ev.title} 
                          className="event-thumb-img" 
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/50x50?text=No+Img';
                          }}
                        />
                        <span className="event-title-text" title={ev.title}>{ev.title}</span>
                      </div>
                    </td>
                    <td className="nowrap-cell">{formatDate(ev.created_on)}</td>
                    <td className="nowrap-cell">{formatDate(ev.updated_on)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <div className="inline-actions-container" style={{ justifyContent: 'center' }}>
                        <button 
                          className="btn-inline-action edit-action" 
                          onClick={() => setEditTarget(ev)}
                          title="Edit Event"
                        >
                          <FaEdit size={13} />
                        </button>
                        <button 
                          className="btn-inline-action delete-action" 
                          onClick={(e) => triggerDeleteConfirm(e, ev)}
                          title="Delete Event"
                        >
                          <FaTrash size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Pagination Controls */}
          {filteredEvents.length > 0 && (
            <div className="premium-pagination-container" style={{ margin: '1.5rem 0 0 0' }}>
              <div className="premium-pagination-info">
                Showing <span>{indexOfFirstItem + 1}</span> to <span>{Math.min(indexOfLastItem, filteredEvents.length)}</span> of <span>{filteredEvents.length}</span> entries
              </div>
              <div className="premium-pagination-list">
                <button 
                  className="premium-pagination-btn arrow-btn" 
                  onClick={() => setCurrentPage(1)} 
                  disabled={currentPage === 1}
                >
                  &laquo;
                </button>
                <button 
                  className="premium-pagination-btn arrow-btn" 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                  disabled={currentPage === 1}
                >
                  &lsaquo;
                </button>
                
                {Array.from({ length: Math.ceil(filteredEvents.length / itemsPerPage) }, (_, idx) => idx + 1)
                  .filter(page => {
                    const totalPages = Math.ceil(filteredEvents.length / itemsPerPage);
                    return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                  })
                  .map((page, index, array) => {
                    return (
                      <React.Fragment key={page}>
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span style={{ color: 'rgba(196, 168, 130, 0.4)', padding: '0 0.25rem' }}>...</span>
                        )}
                        <button 
                          className={`premium-pagination-btn ${currentPage === page ? 'active' : ''}`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  })}

                <button 
                  className="premium-pagination-btn arrow-btn" 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredEvents.length / itemsPerPage)))} 
                  disabled={currentPage === Math.ceil(filteredEvents.length / itemsPerPage)}
                >
                  &rsaquo;
                </button>
                <button 
                  className="premium-pagination-btn arrow-btn" 
                  onClick={() => setCurrentPage(Math.ceil(filteredEvents.length / itemsPerPage))} 
                  disabled={currentPage === Math.ceil(filteredEvents.length / itemsPerPage)}
                >
                  &raquo;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modals */}
      <ConfirmModal
        isOpen={!!editTarget}
        type="edit"
        title="Modify Event"
        itemName={editTarget?.title}
        itemType="event"
        onConfirm={handleConfirmEdit}
        onCancel={() => setEditTarget(null)}
      />

      <ConfirmModal
        isOpen={!!deleteTarget}
        type="delete"
        title="Delete Event"
        itemName={deleteTarget?.title}
        itemType="event"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
};

export default EventList;
